import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Analytics } from './Analytics';
import { TrackerDashboard } from './TrackerDashboard';

type View = 'tracker' | 'ads';

const STORAGE_KEY = 'dashboard.view';

const VIEWS: { key: View; label: string; hint: string }[] = [
  { key: 'tracker', label: 'Tracker', hint: 'Clicks, registrations, deposits and revenue by app, geo and offer' },
  { key: 'ads', label: 'Ads', hint: 'In-app ad delivery: impressions, clicks, completes and referrals' },
];

/**
 * The admin home. Tracker is the default view — it is the money funnel — and the
 * older ad-delivery report sits behind the switch rather than being deleted: it
 * is still the only place in-app impressions and completes are reported.
 */
export function Home() {
  const [params, setParams] = useSearchParams();
  const urlView = params.get('view');
  const view: View = urlView === 'ads' ? 'ads'
    : urlView === 'tracker' ? 'tracker'
    : (localStorage.getItem(STORAGE_KEY) as View | null) === 'ads' ? 'ads'
    : 'tracker';

  useEffect(() => { localStorage.setItem(STORAGE_KEY, view); }, [view]);

  function switchTo(v: View) {
    const p = new URLSearchParams(params);
    p.set('view', v);
    setParams(p, { replace: true });
  }

  const active = VIEWS.find((v) => v.key === view)!;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-content">Dashboard</h1>
          <p className="text-sm text-content-subtle mt-0.5">{active.hint}</p>
        </div>
        <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5" role="tablist">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              role="tab"
              aria-selected={view === v.key}
              onClick={() => switchTo(v.key)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${
                view === v.key ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'tracker' ? <TrackerDashboard /> : <Analytics />}
    </div>
  );
}
